import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { incidentsKeys, sstSupervisionsApi, studentDirectoryKeys } from '@/services';

export const sstSupervisionsKeys = {
  all: ['sst-supervisions'] as const,
  byStudent: (studentId: string) => ['sst-supervisions', studentId] as const,
};

export function useStudentSstSupervisionsQuery(studentId: string | undefined) {
  return useQuery({
    queryKey: sstSupervisionsKeys.byStudent(studentId ?? ''),
    queryFn: () => sstSupervisionsApi.list(studentId!),
    enabled: !!studentId,
  });
}

function useInvalidateStudentSstSupervisions() {
  const queryClient = useQueryClient();
  return (studentId: string) => {
    void queryClient.invalidateQueries({ queryKey: sstSupervisionsKeys.byStudent(studentId) });
    // Student list and detail share this prefix; both carry the active-state badge.
    void queryClient.invalidateQueries({ queryKey: studentDirectoryKeys.all });
    void queryClient.invalidateQueries({ queryKey: ['admin', 'sand-dollars', 'students'] });
    // Every incident payload carries `underSstSupervision` for its students, so a cached list or
    // detail would keep showing the shield — and keep raising the follow-up warning — without this.
    void queryClient.invalidateQueries({ queryKey: incidentsKeys.all });
  };
}

export function useStartStudentSstSupervisionMutation() {
  const invalidate = useInvalidateStudentSstSupervisions();
  return useMutation({
    mutationFn: ({ studentId, reason }: { studentId: string; reason: string }) =>
      sstSupervisionsApi.start(studentId, reason),
    onSuccess: (_data, { studentId }) => invalidate(studentId),
  });
}

export function useEndStudentSstSupervisionMutation() {
  const invalidate = useInvalidateStudentSstSupervisions();
  return useMutation({
    mutationFn: ({ studentId, note }: { studentId: string; note?: string }) => sstSupervisionsApi.end(studentId, note),
    onSuccess: (_data, { studentId }) => invalidate(studentId),
  });
}
